import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Brain, Activity, Calendar, Lock, Sparkles, ArrowRight, Globe, Check, Heart, Zap, Crosshair, Users, Loader2, Linkedin, Instagram } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import TestimonialsCarousel from "../components/TestimonialsCarousel";
import HeroBeat from "../components/HeroBeat";
import InstallBadgesRow from "../components/InstallBadgesRow";
import { useSEO } from "../hooks/useSEO";
import { trackRegisterClick, trackSocialFollowClick } from "../lib/analytics";

export default function LandingPage() {
  const { t, language, setLang, appConfig } = useApp();
  const [params] = useSearchParams();
  const referredFromReport = params.get("via") === "report";

  useSEO(
    language === "es"
      ? {
          title: "BScanIA — Mentor IA para decisiones difíciles · Coach IA emocional y estratégico",
          description: "Tu mentor personal con inteligencia artificial. Pausa antes de actuar, detecta patrones que no ves y decide con claridad emocional y estratégica. IA para ejecutivos, managers, estudiantes y creativos. Bilingüe ES/EN.",
          canonical: "https://bscan.app/",
          lang: "es",
          ogImage: "https://bscan.app/og-image.jpg",
          keywords: "IA para tomar decisiones, mentor IA, coach de decisiones IA, asistente personal IA, inteligencia emocional IA, claridad emocional, coach emocional IA, IA para ejecutivos, IA para managers, IA para liderazgo, IA para estudiantes, IA para artistas, asistente emocional IA, IA para productividad, autoconocimiento con IA, patrones emocionales IA, crecimiento personal IA, claridad mental IA, BScanIA, mente de B",
        }
      : {
          title: "BScanIA — Personal AI Mentor for Hard Decisions · AI Decision Coach",
          description: "Your personal AI mentor. Pause before you act, see the patterns you can't see, and decide with emotional and strategic clarity. AI for executives, managers, students and creatives. Bilingual EN/ES.",
          canonical: "https://bscan.app/?lang=en",
          lang: "en",
          ogImage: "https://bscan.app/og-image-en.jpg",
          keywords: "decision making AI, AI decision assistant, personal AI mentor, AI for executives, AI for managers, leadership AI, AI emotional assistant, emotional intelligence app, productivity AI, AI for students, AI for artists, AI clarity engine, AI life assistant, multi-agent AI platform, AI companion app, BScanIA, B's Mind",
        }
  );

  // Persist attribution so registration can credit the referrer later
  React.useEffect(() => {
    if (referredFromReport) {
      try {
        localStorage.setItem("bscan_referral", JSON.stringify({
          source: "report",
          first_seen_at: localStorage.getItem("bscan_referral")
            ? JSON.parse(localStorage.getItem("bscan_referral")).first_seen_at
            : new Date().toISOString(),
        }));
      } catch (e) { console.warn("[BScanIA] referral localStorage write failed:", e); }

      // Track visit on the backend (once per session)
      try {
        const sessionKey = "bscan_referral_visit_tracked";
        if (!sessionStorage.getItem(sessionKey)) {
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referral/track`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ source: "report", meta: { lang: language } }),
          }).then(() => {
            sessionStorage.setItem(sessionKey, "1");
          }).catch((e) => console.warn("[BScanIA] referral tracking failed (non-critical):", e));
        }
      } catch (e) { console.warn("[BScanIA] sessionStorage unavailable:", e); }
    }
  }, [referredFromReport, language]);

  // Public testimonials — show carousel only when there are >= 3 admin-curated public ones
  const [testimonials, setTestimonials] = React.useState([]);
  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials/public`)
      .then((r) => r.ok ? r.json() : [])
      .then((data) => Array.isArray(data) ? setTestimonials(data) : setTestimonials([]))
      .catch((e) => console.warn("[BScanIA] testimonials fetch failed:", e));
  }, []);
  const showTestimonials = testimonials.length >= 3;

  // Live social-proof — only renders when >= 10 real people helped in last 7 days
  const [socialProof, setSocialProof] = React.useState(null);
  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats/social-proof`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => setSocialProof(d))
      .catch(() => { /* silent — just hide the badge */ });
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#006CFF]/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute top-1/3 right-0 w-[600px] h-[600px] bg-[#7C3AED]/10 rounded-full blur-[120px] pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="landing-logo">
            <img
              src="/brand-logo.png"
              alt="BScanIA"
              className="h-24 w-auto"
              width="400"
              height="315"
              fetchpriority="high"
              decoding="async"
            />
          </Link>
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-white/70" data-testid="landing-lang">
                  <Globe className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                  <span className="font-mono text-xs">{language.toUpperCase()}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1A1A1A] border-white/10 text-white">
                <DropdownMenuItem onClick={() => setLang("en")}>English</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLang("es")}>Español</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/blog" className="hidden sm:inline-flex">
              <Button variant="ghost" className="text-white/70 hover:text-white" data-testid="header-blog-link">
                {language === "es" ? "Blog" : "Blog"}
              </Button>
            </Link>
            <Link to="/about" className="hidden sm:inline-flex">
              <Button variant="ghost" className="text-white/70 hover:text-white" data-testid="header-about-link">
                {language === "es" ? "Acerca" : "About"}
              </Button>
            </Link>
            <Link to="/install" className="hidden sm:inline-flex">
              <Button variant="ghost" className="text-white/70 hover:text-white" data-testid="header-install-link">
                {language === "es" ? "Instalar app" : "Install app"}
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" className="text-white/80 hover:text-white" data-testid="header-login-btn">{t("landing.cta_login")}</Button>
            </Link>
            <Link to="/try" onClick={() => trackRegisterClick("header")}>
              <Button className="bg-[#006CFF] hover:bg-[#0052CC] text-white" data-testid="header-cta-btn">{t("landing.cta_start")}</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-32">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            {referredFromReport ? (
              <div
                className="inline-flex items-start gap-3 px-4 py-3 rounded-xl border border-[#006CFF]/40 bg-gradient-to-r from-[#006CFF]/15 via-[#7C3AED]/10 to-transparent mb-7 fade-in-up max-w-xl"
                data-testid="referred-banner"
              >
                <div className="w-8 h-8 rounded-md bg-[#006CFF]/20 border border-[#006CFF]/40 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-4 h-4 text-[#006CFF]" strokeWidth={2} fill="#006CFF" fillOpacity={0.2} />
                </div>
                <div className="text-left">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#006CFF] mb-1">
                    {language === "es" ? "Alguien confió en ti" : "Someone trusted you"}
                  </div>
                  <p className="text-sm text-white/85 leading-snug">
                    {language === "es"
                      ? "Acaban de compartir contigo su reporte BScanIA. Esto es lo que ven sobre sí mismos cada semana."
                      : "They just shared their BScanIA report with you. This is what they see about themselves every week."}
                  </p>
                </div>
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 mb-6 fade-in-up">
                <span className="w-2 h-2 rounded-full bg-[#00FF88] animate-pulse"></span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">Personal AI Mentor</span>
              </div>
            )}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05] fade-in-up" style={{ animationDelay: "0.1s" }}>
              {referredFromReport
                ? (language === "es"
                    ? "Lo que ellos ven, también lo puedes ver tú."
                    : "What they see, you can see too.")
                : t("landing.tagline")}
            </h1>
            <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#006CFF] fade-in-up" style={{ animationDelay: "0.15s" }}>
              · Decoding Your Decisions ·
            </div>
            <p className="mt-6 text-lg text-white/65 max-w-xl leading-relaxed fade-in-up" style={{ animationDelay: "0.2s" }}>
              {referredFromReport
                ? (language === "es"
                    ? "BScanIA es el mentor personal con IA que esa persona usa antes de mandar el correo, despedir al empleado o decir lo que va a lamentar. Empieza tu propio escaneo en 3 minutos."
                    : "BScanIA is the personal AI mentor that person uses before sending the email, firing the employee, or saying what they'll regret. Start your own scan in 3 minutes.")
                : t("landing.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3 fade-in-up" style={{ animationDelay: "0.3s" }}>
              <Link to="/try" onClick={() => trackRegisterClick("hero")}>
                <Button size="lg" className="bg-[#006CFF] hover:bg-[#0052CC] text-white px-8 py-6 text-base font-medium" data-testid="hero-cta-btn">
                  {referredFromReport
                    ? (language === "es" ? "Inicia tu escaneo" : "Start your own scan")
                    : t("landing.cta_start")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10 px-8 py-6 text-base" data-testid="hero-login-btn">
                  {t("landing.cta_login")}
                </Button>
              </Link>
            </div>
            {(socialProof?.show || socialProof?.show_decisions) && (
              <div className="mt-5 flex flex-wrap items-center gap-2 fade-in-up" style={{ animationDelay: "0.35s" }} data-testid="hero-social-proof">
                {socialProof?.show && (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00FF88]/8 border border-[#00FF88]/25">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FF88] opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00FF88]" />
                    </span>
                    <span className="text-[#9FFFD0] text-xs font-mono uppercase tracking-[0.15em]">
                      {language === "es"
                        ? `B ya ayudó a ${socialProof.count_7d} personas esta semana`
                        : `B already helped ${socialProof.count_7d} people this week`}
                    </span>
                  </div>
                )}
                {socialProof?.show_decisions && (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#006CFF]/8 border border-[#006CFF]/25" data-testid="hero-decisions-badge">
                    <Sparkles className="w-3 h-3 text-[#006CFF]" strokeWidth={2} />
                    <span className="text-[#9FC9FF] text-xs font-mono uppercase tracking-[0.15em]">
                      {language === "es"
                        ? `${socialProof.decisions_total.toLocaleString("es")} decisiones procesadas`
                        : `${socialProof.decisions_total.toLocaleString("en")} decisions processed`}
                    </span>
                  </div>
                )}
              </div>
            )}
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-white/50 text-sm fade-in-up" style={{ animationDelay: "0.4s" }}>
              <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#00FF88]" /> {t("landing.trial_badge")}</div>
              <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#00FF88]" /> {t("landing.cancel_anytime")}</div>
              <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#00FF88]" /> {t("landing.no_card")}</div>
            </div>

            {/* Install badges — high-conversion CTA below hero */}
            <div className="mt-10 fade-in-up" style={{ animationDelay: "0.5s" }}>
              <InstallBadgesRow variant="default" align="left" utmSource="landing_hero" />
            </div>
          </div>

          {/* Visual */}
          <div className="lg:col-span-5 fade-in-up" style={{ animationDelay: "0.3s" }}>
            <HeroBeat language={language} />
          </div>
        </div>
      </section>

      {/* B Preview demo — the "60 second hook" */}
      <BPreviewDemo />

      {/* The B Engine — visual section with brain visualization */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-20 border-t border-white/5">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 lg:order-2">
            <div className="relative aspect-square max-w-md mx-auto">
              <div className="absolute inset-0 rounded-3xl border border-white/10 overflow-hidden glass" data-testid="brain-engine-visual">
                <img
                  src="https://images.unsplash.com/photo-1764336312138-14a5368a6cd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxnbG93aW5nJTIwYmx1ZSUyMGJyYWluJTIwYXJ0aWZpY2lhbCUyMGludGVsbGlnZW5jZXxlbnwwfHx8fDE3NzgzMzE5OTF8MA&ixlib=rb-4.1.0&q=85"
                  alt={language === "es" ? "Motor de inteligencia de B" : "B's intelligence engine"}
                  className="absolute inset-0 w-full h-full object-cover opacity-85"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-black/60"></div>
                <div className="absolute inset-12 rounded-full border border-[#006CFF]/30">
                  <div className="absolute inset-0 rounded-full radar-sweep opacity-60"></div>
                </div>
                <div className="absolute inset-24 rounded-full border border-white/10"></div>
                <div className="absolute inset-36 rounded-full border border-white/5"></div>
                <div className="absolute top-6 left-6 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                  Scanning · Active
                </div>
                <div className="absolute bottom-6 right-6 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                  ID · 0xBSCAN
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 lg:order-1">
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#006CFF] mb-4">
              {language === "es" ? "/ EL MOTOR" : "/ THE ENGINE"}
            </div>
            <h2 className="font-display font-bold text-3xl lg:text-4xl mb-4">
              {language === "es"
                ? "Inteligencia estratégica calibrada a tu contexto."
                : "Strategic intelligence calibrated to your context."}
            </h2>
            <p className="text-white/65 leading-relaxed">
              {language === "es"
                ? "B lee tu situación a través de tu lente exacta — industria, rol, carga emocional, dinámica organizacional — y responde con precisión quirúrgica. No es una IA genérica: es un mentor estratégico que entiende lo que está en juego antes de que termines de explicarlo."
                : "B reads your situation through your exact lens — industry, role, emotional load, organizational dynamics — and responds with surgical precision. Not a generic AI: a strategic mentor that understands what's at stake before you finish explaining."}
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-24 border-t border-white/5">
        <div className="mb-16">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#006CFF] mb-4">/ 01 — Process</div>
          <h2 className="font-display font-bold text-4xl lg:text-5xl mb-3">{t("landing.how_title")}</h2>
          <p className="text-white/60 text-lg">{t("landing.how_subtitle")}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Activity, title: t("landing.how_1_title"), desc: t("landing.how_1_desc"), n: "01" },
            { icon: Sparkles, title: t("landing.how_2_title"), desc: t("landing.how_2_desc"), n: "02" },
            { icon: Calendar, title: t("landing.how_3_title"), desc: t("landing.how_3_desc"), n: "03" },
          ].map((step, i) => {
            const Icon = step.icon;
            return (
              <div key={i} className="card-hover relative p-8 rounded-2xl bg-[#121214] border border-white/10">
                <div className="font-mono text-xs text-[#006CFF] mb-6">/ {step.n}</div>
                <Icon className="w-7 h-7 text-[#006CFF] mb-5" strokeWidth={1.5} />
                <h3 className="font-display font-bold text-xl mb-3">{step.title}</h3>
                <p className="text-white/60 leading-relaxed text-sm">{step.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Benefits */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-24 border-t border-white/5">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#006CFF] mb-4">/ 02 — Outcomes</div>
        <h2 className="font-display font-bold text-4xl lg:text-5xl mb-12">{t("landing.benefits_title")}</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex items-start gap-4 p-6 rounded-xl bg-[#121214] border border-white/10 card-hover">
              <div className="mt-0.5 w-8 h-8 rounded-md bg-[#006CFF]/10 border border-[#006CFF]/30 flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-[#006CFF]" strokeWidth={2} />
              </div>
              <p className="text-white/85 leading-relaxed">{t(`landing.benefit_${i}`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Public testimonials — only when 3+ admin-featured + opted-in */}
      {showTestimonials && (
        <section className="relative z-10 max-w-6xl mx-auto px-6 py-24 border-t border-white/5" data-testid="testimonials-section">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#006CFF] mb-4">/ 02b — Stories</div>
          <h2 className="font-display font-bold text-4xl lg:text-5xl mb-3">
            {language === "es" ? "Historias reales de miembros BScanIA" : "Real stories from BScanIA members"}
          </h2>
          <p className="text-white/60 text-lg mb-12 max-w-2xl">
            {language === "es"
              ? "Casos curados, compartidos con consentimiento explícito. Nombres modificados cuando se solicitó anonimato."
              : "Curated cases, shared with explicit consent. Names edited when anonymity was requested."}
          </p>
          <TestimonialsCarousel items={testimonials} language={language} />
        </section>
      )}

      {/* Pricing */}
      {!appConfig?.beta_mode && (
        <section id="pricing" className="relative z-10 max-w-5xl mx-auto px-6 py-24 border-t border-white/5">
        <div className="text-center mb-12">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#006CFF] mb-4">/ 03 — Pricing</div>
          <h2 className="font-display font-bold text-4xl lg:text-5xl mb-3">{t("landing.pricing_title")}</h2>
          <p className="text-white/60">{t("landing.pricing_subtitle")}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-8 rounded-2xl bg-[#121214] border border-white/10">
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/50 mb-3">Plan A</div>
            <h3 className="font-display font-bold text-2xl mb-1">{t("landing.plan_monthly")}</h3>
            <div className="my-6">
              <span className="font-display font-bold text-5xl">$19.99</span>
              <span className="text-white/50">{t("landing.per_month")}</span>
            </div>
            <div className="inline-block px-3 py-1 rounded-full border border-[#00FF88]/30 bg-[#00FF88]/10 text-[#00FF88] text-xs font-medium mb-6">
              {t("landing.trial_badge")}
            </div>
            <Link to="/try" className="block">
              <Button className="w-full bg-white/5 hover:bg-white/10 border border-white/20 text-white" data-testid="pricing-monthly-btn">{t("landing.cta_start")}</Button>
            </Link>
          </div>
          <div className="relative p-8 rounded-2xl tracing-beam">
            <div className="relative z-10 bg-[#121214] -m-8 p-8 rounded-2xl">
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#006CFF]">Plan B · Recommended</div>
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-[#006CFF] text-white font-medium">{t("landing.save_percent")}</span>
              </div>
              <h3 className="font-display font-bold text-2xl mb-1">{t("landing.plan_annual")}</h3>
              <div className="my-6">
                <span className="font-display font-bold text-5xl">$199</span>
                <span className="text-white/50">{t("landing.per_year")}</span>
              </div>
              <div className="inline-block px-3 py-1 rounded-full border border-[#00FF88]/30 bg-[#00FF88]/10 text-[#00FF88] text-xs font-medium mb-6">
                {t("landing.trial_badge")}
              </div>
              <Link to="/try" className="block">
                <Button className="w-full bg-[#006CFF] hover:bg-[#0052CC] text-white" data-testid="pricing-annual-btn">{t("landing.cta_start")}</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Disclaimer */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-16 border-t border-white/5">
        <div className="p-8 rounded-2xl bg-[#1A1A1A] border border-[#FFB800]/30">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-md bg-[#FFB800]/10 border border-[#FFB800]/30 flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5 text-[#FFB800]" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-display font-bold text-xl mb-3">{t("landing.disclaimer_title")}</h3>
              <p className="text-white/65 leading-relaxed text-sm" data-testid="disclaimer-body">{t("landing.disclaimer_body")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Install CTA banner — final conversion push before footer */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 py-20 border-t border-white/5">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#006CFF]/10 via-[#7C3AED]/8 to-transparent backdrop-blur-sm px-8 sm:px-14 py-12 text-center" data-testid="landing-install-cta">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#006CFF] mb-3">
            {language === "es" ? "Llévate a B en tu bolsillo" : "Take B with you everywhere"}
          </div>
          <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mb-3">
            {language === "es" ? "Instala BScanIA en 10 segundos" : "Install BScanIA in 10 seconds"}
          </h2>
          <p className="text-white/60 max-w-xl mx-auto mb-8 text-sm sm:text-base">
            {language === "es"
              ? "Sin tiendas. Sin esperas. Toca el botón de tu dispositivo y BScanIA aparece en tu pantalla principal como una app nativa."
              : "No app stores. No waiting. Tap your device button and BScanIA lands on your home screen like a native app."}
          </p>
          <InstallBadgesRow variant="compact" align="center" utmSource="landing_footer_cta" />
        </div>
      </section>

      {/* Follow us — social proof + Knowledge Graph signal */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 pb-14">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-sm px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-6" data-testid="landing-follow-us">
          <div className="text-center sm:text-left">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
              {language === "es" ? "Mantente conectado" : "Stay in the loop"}
            </div>
            <h3 className="text-lg sm:text-xl font-display font-semibold tracking-tight">
              {language === "es" ? "Síguenos para insights sobre decisiones difíciles" : "Follow us for insights on hard decisions"}
            </h3>
            <p className="text-white/50 text-xs sm:text-sm mt-1">
              {language === "es" ? "Estrategia, claridad emocional y patrones que sabotean a líderes." : "Strategy, emotional clarity, and patterns that sabotage leaders."}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <a
              href="https://www.linkedin.com/in/BScania/"
              target="_blank"
              rel="me noopener noreferrer"
              aria-label="BScan IA on LinkedIn"
              className="group flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 bg-white/[0.03] text-white/80 hover:text-white hover:bg-white/[0.08] hover:border-white/30 transition-all text-xs font-mono uppercase tracking-[0.15em]"
              data-testid="landing-linkedin-link"
              onClick={() => trackSocialFollowClick("linkedin", "follow_us_section")}
            >
              <Linkedin className="w-4 h-4 text-[#006CFF]" />
              <span>LinkedIn</span>
            </a>
            <a
              href="https://www.instagram.com/bscania.app"
              target="_blank"
              rel="me noopener noreferrer"
              aria-label="BScan IA on Instagram"
              className="group flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 bg-white/[0.03] text-white/80 hover:text-white hover:bg-white/[0.08] hover:border-white/30 transition-all text-xs font-mono uppercase tracking-[0.15em]"
              data-testid="landing-instagram-link"
              onClick={() => trackSocialFollowClick("instagram", "follow_us_section")}
            >
              <Instagram className="w-4 h-4 text-[#E1306C]" />
              <span>Instagram</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <img
            src="/brand-logo.png"
            alt="BScanIA"
            className="h-14 w-auto"
            loading="lazy"
            decoding="async"
          />
          <nav className="flex items-center gap-5 font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">
            <Link to="/blog" className="hover:text-white transition-colors" data-testid="footer-blog-link">
              {language === "es" ? "Blog" : "Blog"}
            </Link>
            <Link to="/about" className="hover:text-white transition-colors" data-testid="footer-about-link">
              {language === "es" ? "Acerca" : "About"}
            </Link>
            <Link to="/terms" className="hover:text-white transition-colors">
              {language === "es" ? "Términos" : "Terms"}
            </Link>
            <Link to="/privacy" className="hover:text-white transition-colors">
              {language === "es" ? "Privacidad" : "Privacy"}
            </Link>
          </nav>
          <div className="flex flex-col items-center md:items-end gap-3">
            {/* Social icons — rel="me" helps Google verify identity ownership */}
            <div className="flex items-center gap-3" aria-label={language === "es" ? "Síguenos en redes" : "Follow us"}>
              <a
                href="https://www.linkedin.com/in/BScania/"
                target="_blank"
                rel="me noopener noreferrer"
                aria-label="BScan IA on LinkedIn"
                title="BScan IA on LinkedIn"
                className="w-9 h-9 flex items-center justify-center rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/30 hover:bg-white/5 transition-all"
                data-testid="footer-linkedin-link"
                onClick={() => trackSocialFollowClick("linkedin", "footer")}
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href="https://www.instagram.com/bscania.app"
                target="_blank"
                rel="me noopener noreferrer"
                aria-label="BScan IA on Instagram"
                title="BScan IA on Instagram"
                className="w-9 h-9 flex items-center justify-center rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/30 hover:bg-white/5 transition-all"
                data-testid="footer-instagram-link"
                onClick={() => trackSocialFollowClick("instagram", "footer")}
              >
                <Instagram className="w-4 h-4" />
              </a>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
              © 2026 · Decoding Your Decisions
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BPreviewDemo — 60-second interactive demo of B answering a typical dilemma.
// Pre-canned responses (no LLM call) so it stays fast, free, and abuse-proof.
// ─────────────────────────────────────────────────────────────────────────────

const DILEMMAS = {
  es: [
    {
      id: "impulse",
      icon: Zap,
      tag: "Impulsividad",
      title: "Mi jefe me copió en un email pasivo-agresivo",
      teaser: "delante de todo el equipo. Quiero responder AHORA con todo lo que pienso.",
      response: `Para. Respira. Lo que estás sintiendo es legítimo, pero lo que vas a responder en este momento es la versión de ti que más vas a lamentar.

Tres cosas:

1. Ese correo tiene público. Tu respuesta también. No estás peleando con él, estás escribiendo frente a 12 personas que van a archivar tus palabras.

2. Lo pasivo-agresivo gana cuando le respondes en su mismo idioma. Pierde cuando respondes con datos.

3. Si en 24 horas sigues queriendo responder, hazlo — pero a él, en privado, sin copia, y con una pregunta, no con una acusación.

¿Qué fue lo concreto que dijo? Porque si te quedas con el tono, no llegas a la solución.`,
    },
    {
      id: "decision",
      icon: Crosshair,
      tag: "Decisión",
      title: "Tengo una oferta nueva con 30% más sueldo",
      teaser: "pero significa empezar de cero en un equipo que no conozco. No duermo.",
      response: `Antes de hablar del 30% — porque ese número es ruido — necesito preguntarte algo:

¿Estás considerando esta oferta porque vas hacia algo, o porque vas huyendo de algo? La respuesta cambia toda la conversación.

Si es porque hay algo en tu trabajo actual que llevas meses tragándote: ese 30% no lo va a arreglar, lo va a posponer. Y en 14 meses estarás en la misma reunión, con menos energía y un sueldo más alto que no compensa.

Si es porque ya tocaste techo y este es el siguiente nivel: el riesgo del "empezar de cero" es real pero finito. 6-9 meses de fricción a cambio de 3-5 años de techo distinto.

¿Qué pasó esta semana que te hizo abrir esa puerta?`,
    },
    {
      id: "politics",
      icon: Users,
      tag: "Política de oficina",
      title: "Mi compañero presentó MI trabajo como suyo",
      teaser: "delante del CEO. Sonreí porque no era el momento. Llevo 6 horas sin poder dormir.",
      response: `La rabia que sientes no es irracional. Es información. Te está diciendo que algo importante fue invadido en público y elegiste no defenderlo en ese momento. Las dos cosas duelen.

Pero ahora estamos en la madrugada y vamos a hacer estrategia, no terapia:

1. No hables con él mañana en caliente. Vas a perder esa conversación.

2. Mañana, en algún momento natural con tu jefe, di una frase exacta: "Quería confirmar contigo que vimos juntos el detalle del proyecto que presentó [nombre]. Quería asegurarme de que tienes visibilidad de las partes en las que yo trabajé." Punto. No expliques más. Esa frase planta una bandera sin pelearse.

3. A partir de mañana, deja rastro por escrito de tu trabajo. No paranoia — higiene.

¿Cuántas veces ha pasado esto antes con esta persona?`,
    },
  ],
  en: [
    {
      id: "impulse",
      icon: Zap,
      tag: "Impulsivity",
      title: "My boss CC'd me in a passive-aggressive email",
      teaser: "in front of the whole team. I want to reply RIGHT NOW with everything I'm thinking.",
      response: `Stop. Breathe. What you're feeling is legitimate, but what you're about to send is the version of you that you'll regret most.

Three things:

1. That email has an audience. Your reply does too. You're not fighting with him — you're writing in front of 12 people who'll archive your words.

2. Passive-aggressive wins when you answer it in its own language. It loses when you answer with data.

3. If in 24 hours you still want to reply, do it — but to him, privately, no CC, and with a question, not an accusation.

What did he actually say? Because if you stay focused on the tone, you'll never reach the solution.`,
    },
    {
      id: "decision",
      icon: Crosshair,
      tag: "Decision",
      title: "I have a new offer with 30% higher salary",
      teaser: "but it means starting from zero on a team I don't know. I can't sleep.",
      response: `Before we talk about the 30% — because that number is noise — I need to ask you something:

Are you considering this offer because you're moving toward something, or because you're running from something? The answer changes the entire conversation.

If it's because there's something at your current job you've been swallowing for months: that 30% won't fix it, it'll postpone it. In 14 months you'll be in the same meeting, with less energy and a higher salary that doesn't compensate.

If it's because you've hit the ceiling and this is the next level: the "starting from zero" risk is real but finite. 6-9 months of friction in exchange for 3-5 years of a different ceiling.

What happened this week that made you open that door?`,
    },
    {
      id: "politics",
      icon: Users,
      tag: "Office politics",
      title: "My coworker presented MY work as his own",
      teaser: "in front of the CEO. I smiled because it wasn't the moment. I haven't slept in 6 hours.",
      response: `The anger you feel isn't irrational. It's information. It's telling you something important was invaded in public and you chose not to defend it then. Both things hurt.

But right now it's the middle of the night and we're doing strategy, not therapy:

1. Don't talk to him tomorrow while you're still hot. You'll lose that conversation.

2. Tomorrow, in some natural moment with your boss, say one exact line: "Quick check — I wanted to confirm you have visibility on the parts of the project [name] presented that I worked on directly." Period. Don't explain further. That line plants a flag without picking a fight.

3. From tomorrow on, leave a written trail of your work. Not paranoia — hygiene.

How many times has this happened before with this person?`,
    },
  ],
};

function BPreviewDemo() {
  const { language } = useApp();
  const dilemmas = DILEMMAS[language] || DILEMMAS.en;
  const [selectedId, setSelectedId] = React.useState(null);
  const [typing, setTyping] = React.useState(false);
  const [visibleText, setVisibleText] = React.useState("");

  const selected = dilemmas.find((d) => d.id === selectedId);

  React.useEffect(() => {
    if (!selected) {
      setVisibleText("");
      return;
    }
    setTyping(true);
    setVisibleText("");
    const full = selected.response;
    let i = 0;
    // Average typing speed feels human at ~18ms per char, with a small pause on newlines
    const interval = setInterval(() => {
      i += 1;
      setVisibleText(full.slice(0, i));
      if (i >= full.length) {
        clearInterval(interval);
        setTyping(false);
      }
    }, 14);
    return () => clearInterval(interval);
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const t1 = language === "es"
    ? { kicker: "/ Try B for 60 seconds", title: "Esto es lo que escucharías de B.", subtitle: "Elige un momento real. Sin registro, sin trampa. Solo B respondiéndote." }
    : { kicker: "/ Try B for 60 seconds", title: "This is what B sounds like.", subtitle: "Pick a real moment. No signup, no catch. Just B answering you." };

  const ctaText = language === "es"
    ? "Cuando ESTO te pase a ti de verdad, ya tendrás a B"
    : "When THIS happens to you for real, you'll already have B";
  const ctaBtn = language === "es" ? "Empezar gratis" : "Start free";
  const skipBtn = language === "es" ? "Probar otro caso" : "Try another";
  const thinking = language === "es" ? "B está pensando…" : "B is thinking…";
  const byline = language === "es" ? "B · mentora estratégica" : "B · strategic mentor";

  return (
    <section
      className="relative z-10 max-w-5xl mx-auto px-6 py-24 border-t border-white/5"
      data-testid="b-preview-demo"
      id="try-b"
    >
      <div className="mb-12">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#006CFF] mb-4">{t1.kicker}</div>
        <h2 className="font-display font-bold text-4xl lg:text-5xl mb-3 leading-[1.05]">{t1.title}</h2>
        <p className="text-white/60 text-lg max-w-2xl">{t1.subtitle}</p>
      </div>

      {!selected && (
        <div className="grid md:grid-cols-3 gap-4" data-testid="b-preview-cards">
          {dilemmas.map((d) => {
            const Icon = d.icon;
            return (
              <button
                key={d.id}
                onClick={() => setSelectedId(d.id)}
                className="group relative text-left p-6 rounded-2xl bg-[#121214] border border-white/10 hover:border-[#006CFF]/60 hover:bg-[#141420] transition-colors duration-200 card-hover"
                data-testid={`b-preview-dilemma-${d.id}`}
              >
                <div className="flex items-center gap-2 mb-5">
                  <Icon className="w-4 h-4 text-[#006CFF]" strokeWidth={1.5} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">{d.tag}</span>
                </div>
                <h3 className="font-display font-semibold text-lg leading-snug mb-2 text-white/95">
                  "{d.title}
                </h3>
                <p className="text-white/55 text-sm leading-relaxed">{d.teaser}"</p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-[#006CFF] text-xs font-mono uppercase tracking-[0.15em] group-hover:gap-2.5 transition-all">
                  {language === "es" ? "Ver respuesta de B" : "See B's answer"}
                  <ArrowRight className="w-3 h-3" strokeWidth={2} />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <div className="space-y-6" data-testid="b-preview-conversation">
          {/* User message */}
          <div className="flex justify-end">
            <div className="max-w-[80%] px-5 py-4 rounded-2xl rounded-tr-md bg-white/8 border border-white/10">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2">
                {language === "es" ? "Tú" : "You"}
              </div>
              <p className="text-white/90 leading-relaxed">
                "{selected.title}, {selected.teaser}"
              </p>
            </div>
          </div>

          {/* B response */}
          <div className="flex justify-start">
            <div className="max-w-[88%] px-5 py-5 rounded-2xl rounded-tl-md bg-gradient-to-br from-[#006CFF]/10 via-[#121214] to-[#121214] border border-[#006CFF]/30">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#006CFF] to-[#7C3AED] flex items-center justify-center">
                  <span className="font-display font-bold text-xs text-white">B</span>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">{byline}</span>
                {typing && <Loader2 className="w-3 h-3 text-[#006CFF] animate-spin ml-1" strokeWidth={2} />}
              </div>
              <div
                className="text-white/88 leading-[1.65] whitespace-pre-wrap font-normal"
                data-testid="b-preview-response-text"
              >
                {visibleText}
                {typing && <span className="inline-block w-[3px] h-[1.1em] bg-[#006CFF] ml-0.5 align-text-bottom animate-pulse" />}
              </div>
              {!typing && (
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-white/35">
                  {language === "es" ? "Demo — la conversación real tiene memoria, voz y reporte" : "Demo — the real chat has memory, voice, and reports"}
                </p>
              )}
            </div>
          </div>

          {/* Post-response CTA */}
          {!typing && (
            <div
              className="mt-10 p-7 rounded-2xl bg-gradient-to-br from-[#006CFF]/15 via-[#7C3AED]/8 to-transparent border border-[#006CFF]/35 text-center"
              data-testid="b-preview-cta-block"
            >
              <p className="font-display font-bold text-xl lg:text-2xl leading-snug mb-5 max-w-xl mx-auto">
                {ctaText}
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Link to="/try">
                  <Button
                    size="lg"
                    className="bg-[#006CFF] hover:bg-[#0052CC] text-white px-7 py-5 text-base font-medium"
                    data-testid="b-preview-cta-register-btn"
                  >
                    {ctaBtn} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  onClick={() => setSelectedId(null)}
                  className="text-white/65 hover:text-white hover:bg-white/5"
                  data-testid="b-preview-try-another-btn"
                >
                  {skipBtn}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
